import { Inject, Injectable } from '@angular/core';
import { Temporal } from '@js-temporal/polyfill';
import { AppStartupService, TicketWithProductInfo } from '@ridango/core';
import { combineLatest, Observable } from 'rxjs';
import { distinctUntilChanged, filter, map, shareReplay, tap } from 'rxjs/operators';
import Env from '@ridango/core/env';
import { ProductsFacade } from '@ridango/components';

@Injectable({
    providedIn: 'root'
})
export class DateCalculationService {
    private requestedPurchaseProducts = false;
    private products$ = combineLatest([this.productsFacade.products$, this.productsFacade.loading$]).pipe(
        tap(([products, loading]) => {
            if (!this.requestedPurchaseProducts && !loading && products.length === 0) {
                this.requestedPurchaseProducts = true;
                this.productsFacade.fetchPurchaseProducts(this.env.CHANNEL);
            }
        }),
        map(([products]) => products),
        shareReplay({ bufferSize: 1, refCount: true })
    );

    constructor(
        @Inject('ENV') private env: Env,
        private appStartupService: AppStartupService,
        private productsFacade: ProductsFacade
    ) {}

    calculateAdjustedEndingDate(
        endingDate: string | Date | undefined,
        timezone: string,
        productType?: string,
        productLength?: number
    ): string | undefined {
        // Skip adjustment for short-duration tickets (single tickets with duration <= 24 hours)
        // These tickets should display their exact ending time, not snapped to 23:59
        // productLength is in minutes (e.g., 180 for 3 hours). 24 hours = 1440 minutes.
        const shouldSkipOffset = this.env.PURCHASE?.SKIP_OFFSET_FOR_DURATION_BASED_TICKETS ?? false;
        const isDurationBasedTicket = productType && ['M', 'H'].includes(productType);
        const isShortDuration = productLength !== undefined && productLength <= 1440;

        if (shouldSkipOffset && (isDurationBasedTicket || isShortDuration)) {
            return endingDate instanceof Date ? endingDate.toISOString() : (endingDate as string);
        }

        try {
            let zonedDateTime;
            let instant;

            if (endingDate instanceof Date) {
                instant = Temporal.Instant.from(endingDate.toISOString());
                zonedDateTime = instant.toZonedDateTimeISO(timezone);
            } else {
                const dateString = endingDate as string;

                if (dateString.endsWith('Z')) {
                    instant = Temporal.Instant.from(dateString);
                    zonedDateTime = instant.toZonedDateTimeISO(timezone);
                } else {
                    try {
                        zonedDateTime = Temporal.ZonedDateTime.from(dateString);
                        instant = zonedDateTime.toInstant();
                    } catch (e) {
                        const plainDateTime = Temporal.PlainDateTime.from(dateString);
                        zonedDateTime = plainDateTime.toZonedDateTime(timezone);
                        instant = zonedDateTime.toInstant();
                    }
                }
            }

            const adjustedZonedDateTime = zonedDateTime.subtract(
                this.appStartupService.getTransportationDayOffset(instant)
            );

            const year = adjustedZonedDateTime.year;
            const month = adjustedZonedDateTime.month;
            const day = adjustedZonedDateTime.day;

            const isoDate = `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}T23:59:00`;
            const plainDateTime = Temporal.PlainDateTime.from(isoDate);
            const zonedResult = plainDateTime.toZonedDateTime(timezone);
            return zonedResult.toInstant().toString();
        } catch (error) {
            return endingDate as unknown as string;
        }
    }

    getAdjustedEndingDate$<T extends TicketWithProductInfo>(
        ticket$: Observable<T | undefined>,
        timezone: string
    ): Observable<string | undefined> {
        return combineLatest([ticket$, this.products$]).pipe(
            filter(([ticket]) => ticket !== undefined),
            map(([ticket, products]) => {
                const endingDate = ticket.endDate ?? ticket.ending;

                const productId =
                    ticket.productId ??
                    ticket.product_id ??
                    ticket.product?.product_id ??
                    ticket.product_object?.product_id;

                const productIdNum = Number(productId);
                const matchedProduct = Number.isFinite(productIdNum)
                    ? products.find((p) => Number(p.product_id) === productIdNum)
                    : undefined;

                const productType =
                    ticket.product_type ??
                    ticket.product?.product_type ??
                    ticket.product_object?.product_type ??
                    matchedProduct?.product_type;

                let productLength =
                    ticket.product?.product_length ??
                    ticket.product_object?.product_length ??
                    matchedProduct?.product_length;

                if (!productLength && ticket.starting && endingDate) {
                    const hasOffset = (s: string) => s.includes('+') || s.includes('Z');

                    const start = hasOffset(ticket.starting as string)
                        ? Temporal.Instant.from(ticket.starting as string)
                        : Temporal.ZonedDateTime.from(`${ticket.starting}[${timezone}]`).toInstant();

                    const end = hasOffset(endingDate as string)
                        ? Temporal.Instant.from(endingDate as string)
                        : Temporal.ZonedDateTime.from(`${endingDate}[${timezone}]`).toInstant();

                    const diffSeconds = end.epochSeconds - start.epochSeconds;
                    productLength = Math.round(diffSeconds / 60);
                }

                return this.calculateAdjustedEndingDate(endingDate, timezone, productType, productLength);
            }),
            distinctUntilChanged(),
            shareReplay({ bufferSize: 1, refCount: true })
        );
    }
}
